import type { KeyboardEvent, MouseEvent, RefObject } from 'react';
import React, { useState, useRef } from 'react';

import { twMerge } from 'tailwind-merge';

import type { IconProps } from 'components/icon/icon';

import Icon from 'components/icon/icon';

interface RatingProps {
  iconProps?: Omit<IconProps, 'icon'>;
  iconClassName?: string;
  className?: string;
  totalStars?: number;
  onChange?: (rate: number) => void;
  activeStarColor?: string;
  emptyStarColor?: string;
  name?: string;
  readonly?: boolean;
  initialValue?: number;
}

const StarRating: React.FC<RatingProps> = (props) => {
  const precision = 0.5;
  const {
    iconProps,
    totalStars = 5,
    activeStarColor = 'text-yellow stroke-yellow stroke-[20px]',
    onChange,
    className,
    emptyStarColor = 'text-gray100 stroke-gray300 stroke-[20px]',
    initialValue,
  } = props;
  const [activeStar, setActiveStar] = useState(initialValue ?? -1);
  const [hoverActiveStar, setHoverActiveStar] = useState(-1);
  const [isHovered, setIsHovered] = useState(false);

  const ratingRef: RefObject<HTMLDivElement> = useRef(null);

  const percentage = (e: MouseEvent<HTMLDivElement>) => {
    if (ratingRef.current) {
      const { width, x } = ratingRef.current.getBoundingClientRect();
      const percent = (e.clientX - x) / width;
      const numberInStars = percent * totalStars;
      const nearestNumber =
        Math.round((numberInStars + precision / 2) / precision) * precision;
      return Number(
        nearestNumber.toFixed(precision.toString().split('.')[1]?.length || 0),
      );
    }
    return 0;
  };

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    setIsHovered(false);
    setActiveStar(percentage(e));
    onChange?.(percentage(e));
  };

  const onMouseMoved = (e: MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
    setHoverActiveStar(percentage(e));
  };

  const onMouseLeft = (_e: MouseEvent<HTMLDivElement>) => {
    setHoverActiveStar(-1);
    setIsHovered(false);
  };

  return (
    <div
      ref={ratingRef}
      className={twMerge(
        'inline-flex justify-center relative cursor-pointer text-left',
        props.readonly && 'pointer-events-none',
        className,
      )}
      onMouseMove={onMouseMoved}
      onMouseLeave={onMouseLeft}
      onClick={onClick}
      onKeyDown={(_evt: KeyboardEvent<HTMLDivElement>) => {}}
      role="button"
      tabIndex={-42}
    >
      {[...Array.from({ length: totalStars }, (x, i) => i)].map(
        (key: number, index: number) => {
          const activeState = isHovered ? hoverActiveStar : activeStar;
          let activePercentage: string;
          if (activeState >= index + 1) activePercentage = 'w-full';
          else {
            activePercentage =
              Math.ceil(activeState) === index + 1 && activeState !== index + 1
                ? 'w-1/2'
                : 'w-0';
          }
          const showEmptyIcon = activeState === -1 || activeState < index + 1;
          const starContainerClassName = `overflow-hidden absolute ${activePercentage}`;
          return (
            <div key={key} className="relative cursor-pointer">
              <div className={twMerge(starContainerClassName, activeStarColor)}>
                <Icon
                  icon="star"
                  {...iconProps}
                  className={twMerge(iconProps?.className)}
                />
              </div>
              <div className={showEmptyIcon ? emptyStarColor : activeStarColor}>
                <Icon
                  icon="star"
                  {...iconProps}
                  className={twMerge(iconProps?.className)}
                />
              </div>
            </div>
          );
        },
      )}
    </div>
  );
};

export default StarRating;
